import { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useAdminAuth } from './AdminAuthContext';
import { useAdminTheme } from './AdminThemeContext';
import axios from 'axios';
import { 
  TrendingUp,
  Users,
  MoreHorizontal,
  ChevronRight,
  Loader2,
  BarChart3,
  X,
  Calendar,
  ArrowUpRight,
  CheckCircle2
} from 'lucide-react';
import { 
  ComposedChart,
  Bar,
  Line,
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Network logos
const MTN_LOGO = "https://upload.wikimedia.org/wikipedia/commons/thumb/9/93/New-mtn-logo.jpg/1200px-New-mtn-logo.jpg";
const ORANGE_LOGO = "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c8/Orange_logo.svg/1200px-Orange_logo.svg.png";

export const AdminDashboard = () => {
  const { isDarkMode } = useAdminTheme();
  const { admin, user } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('All');
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, txRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/transactions?limit=5`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setTransactions(txRes.data?.transactions || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US').format(amount);
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000 / 60);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff} min ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)} hr ago`;
    return `${Math.floor(diff / 1440)} days ago`;
  };

  // Chart data - 30 days with confirmed and volume
  const volumeChartData = Array.from({ length: 30 }, (_, i) => ({
    day: i + 1,
    confirmed: Math.floor(Math.random() * 150000) + 30000 + (i > 15 ? 50000 : 0),
    volume: Math.floor(Math.random() * 200000) + 50000 + (i > 15 ? 80000 : 0),
  }));

  // Pie chart data
  const pieData = [
    { name: 'MTN', value: 61, color: '#5EEAD4' },
    { name: 'Orange', value: 39, color: '#2DD4BF' },
  ];

  // Sample transactions
  const sampleTransactions = [
    { id: '1', customer: 'John Doe', amount: 500, currency: 'LRD', currencyNetwork: 'MTN', network: 'Orange', status: 'confirmed', created_at: new Date(Date.now() - 2*60000) },
    { id: '2', customer: 'Mary Smith', amount: 400, currency: 'LRD', currencyNetwork: 'Orange', network: 'MTN', status: 'pending', created_at: new Date(Date.now() - 5*60000) },
    { id: '3', customer: 'Alex Cooper', amount: 1200, currency: 'LRD', currencyNetwork: 'MTN', network: 'Orange', status: 'confirmed', created_at: new Date(Date.now() - 10*60000) },
    { id: '4', customer: 'Richard T.', amount: 900, currency: 'LRD', currencyNetwork: 'Orange', network: 'MTN', status: 'failed', created_at: new Date(Date.now() - 18*60000) },
    { id: '5', customer: 'Sarah K.', amount: 50, currency: 'USD', currencyNetwork: 'MTN', network: 'Orange', status: 'failed', created_at: new Date(Date.now() - 22*60000) },
  ];

  const displayTransactions = transactions.length > 0 ? transactions.slice(0, 5) : sampleTransactions;
  const filteredTransactions = activeTab === 'All' 
    ? displayTransactions 
    : displayTransactions.filter(tx => tx.status.toLowerCase() === activeTab.toLowerCase());

  // Handle bar click
  const handleBarClick = (data, index) => {
    console.log('Chart clicked:', data, index);
    if (data && data.activePayload && data.activePayload.length > 0) {
      const dayData = data.activePayload[0].payload;
      console.log('Day data:', dayData);
      setSelectedDay(dayData);
    } else if (data && data.payload) {
      // Alternative data structure
      console.log('Alternative payload:', data.payload);
      setSelectedDay(data.payload);
    }
  };

  // Handle individual bar click
  const handleSingleBarClick = (data, index, e) => {
    console.log('Single bar clicked:', data);
    if (data) {
      setSelectedDay(data);
    }
  };

  // Get date for day number (relative to today)
  const getDayDate = (dayNum) => {
    const date = new Date();
    date.setDate(date.getDate() - (30 - dayNum));
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  const totalVolume = stats?.today?.volume || 2450680;
  const totalTransactions = stats?.transactions?.total || 4532;
  const activeUsers = stats?.merchants?.active || 124;

  return (
    <div className="h-full overflow-hidden animate-fadeIn" data-testid="admin-dashboard">
      {/* Main Grid Layout */}
      <div className="grid grid-cols-4 gap-4 h-full">
        {/* Row 1: Stats Cards */}
        <div className={`rounded-xl p-4 shadow-sm border transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 ${
          isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'
        }`} style={{ animationDelay: '50ms' }}>
          <div className="flex justify-between items-start mb-2">
            <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Total Volume</span>
            <BarChart3 className="w-5 h-5 text-cyan-400 transition-transform duration-300 hover:scale-110" />
          </div>
          <p className="text-xl font-bold text-teal-500">LRD {formatCurrency(totalVolume)}</p>
        </div>

        <div className={`rounded-xl p-4 shadow-sm border transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 ${
          isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'
        }`} style={{ animationDelay: '100ms' }}>
          <div className="flex justify-between items-start mb-2">
            <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Transactions</span>
            <TrendingUp className="w-5 h-5 text-cyan-400 transition-transform duration-300 hover:scale-110" />
          </div>
          <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(totalTransactions)}</p>
        </div>

        <div className={`rounded-xl p-4 shadow-sm border transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 ${
          isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'
        }`} style={{ animationDelay: '150ms' }}>
          <div className="flex justify-between items-start mb-2">
            <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Active Users</span>
            <Users className="w-5 h-5 text-cyan-400 transition-transform duration-300 hover:scale-110" />
          </div>
          <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{activeUsers}</p>
        </div>

        {/* SMS Received - Simple stat card */}
        <div className={`rounded-xl p-4 shadow-sm border transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 ${
          isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'
        }`} style={{ animationDelay: '200ms' }}>
          <div className="flex justify-between items-start mb-2">
            <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>SMS Received</span>
            <MoreHorizontal className={`w-5 h-5 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
          </div>
          <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>6,745</p>
        </div>

        {/* Row 2: Transaction Volume Chart (left) + SMS Details (right) */}
        {/* Transaction Volume Chart - Spans 3 cols and 2 rows */}
        <div className={`rounded-xl p-5 shadow-sm border col-span-3 row-span-2 transition-all duration-300 ${
          isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'
        }`} style={{ animationDelay: '250ms' }}>
          <div className="flex justify-between items-start mb-3">
            <div>
              <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Transaction Volume</h3>
              <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>Last 30 Days</p>
            </div>
            <MoreHorizontal className={`w-5 h-5 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
          </div>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart 
                data={volumeChartData} 
                barGap={0}
                onClick={handleBarClick}
                style={{ cursor: 'pointer' }}
              >
                <defs>
                  <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#5EEAD4" stopOpacity={1}/>
                    <stop offset="100%" stopColor="#22D3EE" stopOpacity={0.8}/>
                  </linearGradient>
                  <linearGradient id="barGradientHover" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2DD4BF" stopOpacity={1}/>
                    <stop offset="100%" stopColor="#0EA5E9" stopOpacity={0.9}/>
                  </linearGradient>
                </defs>
                <XAxis 
                  dataKey="day" 
                  axisLine={false} 
                  tickLine={false}
                  tick={{ fill: '#9CA3AF', fontSize: 10 }}
                  interval={1}
                />
                <YAxis 
                  axisLine={false} 
                  tickLine={false}
                  tick={{ fill: '#9CA3AF', fontSize: 10 }}
                  tickFormatter={(value) => value >= 1000 ? `${value/1000}k` : value}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: isDarkMode ? '#1e293b' : '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                  }}
                  formatter={(value) => [value >= 1000 ? `${(value/1000).toFixed(0)}k` : value]}
                  cursor={false}
                />
                <Bar 
                  dataKey="volume" 
                  fill="url(#barGradient)"
                  radius={[4, 4, 0, 0]}
                  barSize={14}
                  className="cursor-pointer"
                  onClick={handleSingleBarClick}
                  onMouseEnter={(data, index, e) => {
                    if (e && e.target) e.target.style.filter = 'brightness(1.1)';
                  }}
                  onMouseLeave={(data, index, e) => {
                    if (e && e.target) e.target.style.filter = 'brightness(1)';
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="confirmed" 
                  stroke="#0EA5E9" 
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 6, fill: '#0EA5E9', stroke: '#fff', strokeWidth: 2, cursor: 'pointer' }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center gap-6 mt-3 pl-32">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-teal-400"></div>
              <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Confirmed</span>
            </div>
            <div className="flex items-center gap-2 ml-16">
              <div className="w-3 h-3 rounded-full bg-sky-500"></div>
              <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Volume</span>
            </div>
          </div>
        </div>

        {/* SMS Details Card with Pie Chart - Spans 2 rows */}
        <div className={`rounded-xl p-4 shadow-sm border row-span-2 transition-all duration-300 flex flex-col ${
          isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'
        }`} style={{ animationDelay: '300ms' }}>
          {/* Pie Chart - Bigger */}
          <div className="flex justify-center flex-1 items-center">
            <div className="relative w-44 h-44">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={80}
                    dataKey="value"
                    startAngle={90}
                    endAngle={-270}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>61%</span>
                <span className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>39%</span>
              </div>
            </div>
          </div>

          {/* Network List */}
          <div className="space-y-2 mt-2">
            <div className="flex items-center gap-2 group">
              <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center transition-transform duration-200 group-hover:scale-105">
                <span className="text-white text-[10px] font-bold">OM</span>
              </div>
              <div className="flex-1">
                <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Orange</p>
              </div>
              <span className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>4,983,200</span>
            </div>
            <div className="flex items-center gap-2 group">
              <img src={MTN_LOGO} alt="MTN" className="w-8 h-8 rounded-lg object-cover transition-transform duration-200 group-hover:scale-105" />
              <div className="flex-1">
                <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>MTN</p>
              </div>
              <span className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>943,480</span>
            </div>
          </div>
        </div>

        {/* Recent Payments Table - Spans all 4 cols */}
        <div className={`rounded-xl shadow-sm border col-span-4 flex flex-col overflow-hidden transition-all duration-300 ${
          isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'
        }`} style={{ animationDelay: '300ms' }}>
          <div className={`flex items-center justify-between px-4 py-3 border-b flex-shrink-0 ${
            isDarkMode ? 'border-slate-700' : 'border-gray-100'
          }`}>
            <div className="flex items-center gap-5">
              <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Recent Payments</h3>
              <div className="flex items-center gap-3">
                {['All', 'Confirmed', 'Pending', 'Failed'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`text-xs font-medium transition-colors duration-200 ${
                      activeTab === tab 
                        ? 'text-teal-500' 
                        : isDarkMode ? 'text-slate-500 hover:text-slate-300' : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>
            <Link to="/admin/transactions" className={`flex items-center gap-1 text-xs transition-colors duration-200 ${
              isDarkMode ? 'text-slate-400 hover:text-teal-400' : 'text-gray-500 hover:text-teal-500'
            }`}>
              View All <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="flex-1 overflow-auto">
            <table className="w-full text-xs">
              <thead className={`sticky top-0 ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                <tr>
                  <th className={`px-4 py-2 text-left font-medium ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}>Customer</th>
                  <th className={`px-4 py-2 text-left font-medium ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}>Amount</th>
                  <th className={`px-4 py-2 text-left font-medium ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}>Currency</th>
                  <th className={`px-4 py-2 text-left font-medium ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}>Network</th>
                  <th className={`px-4 py-2 text-left font-medium ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}>Status</th>
                  <th className={`px-4 py-2 text-left font-medium ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}>Time</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((tx, idx) => (
                  <tr 
                    key={tx.id || idx} 
                    className={`border-b transition-all duration-300 cursor-pointer ${
                      isDarkMode 
                        ? 'border-slate-700 hover:bg-gradient-to-r hover:from-transparent hover:via-slate-700/50 hover:to-transparent' 
                        : 'border-gray-50 hover:bg-gradient-to-r hover:from-transparent hover:via-teal-50/50 hover:to-transparent'
                    }`}
                    style={{ animationDelay: `${idx * 50}ms` }}
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-medium transition-transform duration-200 hover:scale-110"
                          style={{ backgroundColor: ['#60A5FA', '#34D399', '#FBBF24', '#F87171', '#A78BFA'][idx % 5] }}
                        >
                          {(tx.customer || tx.description || 'C')[0]}
                        </div>
                        <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{tx.customer || tx.description || 'Customer'}</span>
                      </div>
                    </td>
                    <td className={`px-4 py-2.5 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {formatCurrency(tx.amount)} {tx.currency || 'LRD'}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <img 
                          src={tx.currencyNetwork === 'MTN' || tx.network === 'MTN' ? MTN_LOGO : ORANGE_LOGO} 
                          alt="" 
                          className="w-5 h-5 rounded object-cover" 
                        />
                        <span className={isDarkMode ? 'text-slate-300' : 'text-gray-600'}>{tx.currencyNetwork || tx.network || 'MTN'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <img 
                          src={tx.network === 'Orange' ? ORANGE_LOGO : MTN_LOGO} 
                          alt="" 
                          className="w-5 h-5 rounded object-cover" 
                        />
                        <span className={isDarkMode ? 'text-slate-300' : 'text-gray-600'}>{tx.network || 'Orange'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <span 
                        className={`inline-flex px-2 py-0.5 rounded text-[10px] font-medium ${
                          tx.status === 'confirmed' ? 'bg-green-100 text-green-600' :
                          tx.status === 'pending' ? 'bg-amber-100 text-amber-600' :
                          'bg-red-100 text-red-600'
                        }`}
                      >
                        {tx.status === 'confirmed' ? 'Confirmed' : tx.status === 'pending' ? 'Pending' : 'Failed'}
                      </span>
                    </td>
                    <td className={`px-4 py-2.5 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>{getTimeAgo(tx.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Day Details Modal */}
      {selectedDay && (
        <div 
          className="fixed inset-0 flex items-center justify-center z-50"
          onClick={() => setSelectedDay(null)}
        >
          <div 
            className={`rounded-2xl p-6 w-full max-w-md shadow-2xl transform transition-all duration-300 scale-100 animate-fadeIn ${
              isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-gray-200'
            }`}
            style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-teal-500/20' : 'bg-teal-50'}`}>
                  <Calendar className="w-5 h-5 text-teal-500" />
                </div>
                <div>
                  <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Day {selectedDay.day} Details
                  </h3>
                  <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    {getDayDate(selectedDay.day)}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setSelectedDay(null)}
                className={`p-2 rounded-lg transition-all duration-200 hover:scale-110 ${
                  isDarkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-gray-100 text-gray-500'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <ArrowUpRight className="w-4 h-4 text-teal-500" />
                  <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Total Volume</span>
                </div>
                <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  LRD {formatCurrency(selectedDay.volume)}
                </p>
              </div>
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-4 h-4 text-sky-500" />
                  <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Confirmed</span>
                </div>
                <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  LRD {formatCurrency(selectedDay.confirmed)}
                </p>
              </div>
            </div>

            {/* Confirmation Rate */}
            <div className={`p-4 rounded-xl mb-6 ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Confirmation Rate</span>
                <span className="text-sm font-semibold text-teal-500">
                  {((selectedDay.confirmed / selectedDay.volume) * 100).toFixed(1)}%
                </span>
              </div>
              <div className={`w-full h-2 rounded-full ${isDarkMode ? 'bg-slate-600' : 'bg-gray-200'}`}>
                <div 
                  className="h-full rounded-full bg-gradient-to-r from-teal-400 to-sky-500 transition-all duration-500"
                  style={{ width: `${(selectedDay.confirmed / selectedDay.volume) * 100}%` }}
                />
              </div>
            </div>

            {/* Action Button */}
            <Link 
              to={`/admin/transactions?date=day-${selectedDay.day}`}
              className="flex items-center justify-center gap-2 w-full py-3 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-medium transition-all duration-200 active:scale-95"
              onClick={() => setSelectedDay(null)}
            >
              View All Transactions
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}

      {/* Animation Styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default AdminDashboard;
